import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { MessageCircle, Phone, Printer, Search } from 'lucide-react';
import { getRecentPatients, searchPatients, type FollowUpDue } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import NewPatientModal from '@/components/Patient/NewPatientModal';
import { formatTime } from '@/lib/formatDate';
import { displayAge } from '@/lib/patientAge';
import { cn } from '@/lib/utils';

// The doctor's home when the queue is quiet: start a visit, see today's prescriptions, follow-ups due.

const ageSex = (age?: number | null, dob?: string | null, gender?: string | null) =>
  [displayAge(age ?? undefined, dob ?? undefined) || null, gender ? gender.charAt(0).toUpperCase() : null].filter(Boolean).join(' ');

const shortDate = (d: string) => format(new Date(`${d.slice(0, 10)}T00:00:00`), 'd MMM');

/** Search a patient (name, phone or MRN) and open a new prescription; "Add a new patient" when nobody matches */
export function StartVisitCard() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();
  const canAddPatient = can('patients:write');
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searched, setSearched] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<any[]>([]);
  const [showNew, setShowNew] = useState(false);
  const term = query.trim();

  // Desktop: ready to type. Tablets and phones: no keyboard popping up on its own
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) inputRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!currentUser?.clinic_id) return;
    getRecentPatients(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id)
      .then(r => setRecent(Array.isArray(r) ? r : []))
      .catch(() => {});
  }, [currentUser?.clinic_id, currentUser?.doctor_id]);

  useEffect(() => {
    setActive(0);
    if (term.length < 2) { setResults([]); setSearched(false); return; }
    const id = setTimeout(() => {
      searchPatients(term, currentUser?.clinic_id)
        .then(r => { setResults((r || []).slice(0, 6)); setSearched(true); })
        .catch(() => { setResults([]); setSearched(true); });
    }, 250);
    return () => clearTimeout(id);
  }, [term, currentUser?.clinic_id]);

  const startFor = (id: string) => navigate(`/patients/${id}?openPrescription=true`);
  const open = term.length >= 2 && searched;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); startFor(results[active].id); }
    else if (e.key === 'Escape') setQuery('');
  };

  return (
    <section aria-label="Start a visit" className="rounded-lg border border-border bg-card px-4 sm:px-5 py-4 mb-5">
      <h2 className="text-[15px] font-semibold text-foreground">Start a visit</h2>
      <div className="mt-3 flex gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Name, phone or MRN"
            aria-label="Find a patient to start a visit"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
            className="w-full h-9 rounded-md border border-field bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {open && (
            <ul id={listId} role="listbox" className="absolute left-0 right-0 top-full mt-1 z-20 rounded-md border border-border bg-popover shadow-md py-1 max-h-80 overflow-y-auto">
              {results.map((p, i) => (
                <li key={p.id} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => startFor(p.id)}
                    className={cn('w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm', i === active && 'bg-muted')}
                  >
                    <span className="min-w-0 truncate">
                      <span className="text-foreground capitalize">{p.name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {[ageSex(p.age, p.date_of_birth, p.gender), p.phone, p.mrn].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-medium text-primary">New Rx</span>
                  </button>
                </li>
              ))}
              {results.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  No patient matches “{term}”.
                  {canAddPatient && (
                    <button type="button" onClick={() => setShowNew(true)} className="ml-1 font-medium text-primary hover:underline underline-offset-2">
                      Add a new patient
                    </button>
                  )}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      {recent.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>Recent:</span>
          {recent.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => startFor(p.id)}
              title={`New prescription for ${p.name}`}
              className="max-w-[12rem] truncate rounded-full border border-border px-2.5 py-0.5 text-foreground capitalize hover:bg-muted transition-colors"
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      <NewPatientModal isOpen={showNew} onClose={() => setShowNew(false)} onPatientCreated={() => setShowNew(false)} initialSearch={term} />
    </section>
  );
}

/** Today's prescriptions, newest first: what was given, and print or share again */
export function SeenTodayCard({ prescriptions, onPrint, className }: {
  prescriptions: any[];
  onPrint: (rx: any) => void;
  className?: string;
}) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? prescriptions : prescriptions.slice(0, 6);

  return (
    <section id="seen-today" aria-label="Seen today" className={cn('rounded-lg border border-border bg-card overflow-hidden min-w-0', className)}>
      <div className="flex items-center h-11 px-4 border-b border-border">
        <h2 className="text-[15px] font-semibold text-foreground">
          Seen today{prescriptions.length > 0 && <span className="font-normal text-muted-foreground tabular-nums"> · {prescriptions.length}</span>}
        </h2>
      </div>
      {prescriptions.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">Prescriptions you write today appear here, ready to print or share again.</p>
      ) : (
        <ol>
          {shown.map(rx => {
            const meds = rx.medicines?.length ?? 0;
            // What the visit was about: the diagnosis, else the complaints
            const about = rx.diagnosis?.trim()
              || (rx.complaints ?? []).map((c: any) => c.complaint?.trim()).filter(Boolean).join(', ') || null;
            const due = rx.next_visit?.date;
            return (
              <li key={rx.id} className="flex items-center gap-3 px-4 py-1.5 min-h-12 border-b border-divider last:border-0">
                <span className="w-16 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{formatTime(rx.created_at)}</span>
                <button type="button" onClick={() => navigate(`/patients/${rx.patient_id}`)} className="min-w-0 flex-1 text-left">
                  <p className="text-sm text-foreground truncate">
                    <span className="capitalize hover:underline underline-offset-2">{rx.patient_name || 'Patient'}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{ageSex(rx.patient_age, rx.patient_date_of_birth, rx.patient_gender)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[
                      about,
                      meds ? `${meds} medicine${meds === 1 ? '' : 's'}` : null,
                      due ? `follow-up ${shortDate(due)}` : null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                </button>
                <Button
                  variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0"
                  onClick={() => onPrint(rx)}
                  title="Print or share" aria-label={`Print or share ${rx.patient_name}'s prescription`}
                >
                  <Printer className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ol>
      )}
      {prescriptions.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="w-full h-9 px-4 border-t border-border text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
        >
          {showAll ? 'Show fewer' : `Show all ${prescriptions.length}`}
        </button>
      )}
    </section>
  );
}

/** A follow-up in "Expected today": the last diagnosis, and a prescription when they come in */
export function FollowUpRow({ followUp, canPrescribe }: { followUp: FollowUpDue; canPrescribe: boolean }) {
  const navigate = useNavigate();
  return (
    <li className="flex items-center gap-3 px-4 py-1.5 min-h-10 border-b border-divider">
      <span className="w-16 shrink-0 text-xs font-medium text-primary">Follow-up</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground truncate">
          <button type="button" onClick={() => navigate(`/patients/${followUp.patient_id}`)} className="capitalize hover:underline underline-offset-2">
            {followUp.patient_name}
          </button>
          <span className="ml-2 text-xs text-muted-foreground">{ageSex(followUp.patient_age, followUp.patient_date_of_birth, followUp.patient_gender)}</span>
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {[followUp.diagnosis?.trim() || null, `last seen ${shortDate(followUp.last_visit_at)}`].filter(Boolean).join(' · ')}
        </p>
      </div>
      <ContactButtons followUp={followUp} />
      {canPrescribe && (
        <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs shrink-0" onClick={() => navigate(`/patients/${followUp.patient_id}?openPrescription=true`)}>
          Start
        </Button>
      )}
    </li>
  );
}

/** Follow-ups from the last 7 days that didn't come back: folded away, with a call or a WhatsApp reminder
 *  (and, for the front desk, whose patient it is and a Book button) */
export function MissedFollowUps({ followUps, showDoctor, onBook }: {
  followUps: FollowUpDue[];
  showDoctor?: boolean;
  onBook?: (followUp: FollowUpDue) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (followUps.length === 0) return null;
  return (
    <div id="missed-follow-ups">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="w-full h-9 px-4 inline-flex items-center gap-1.5 text-xs font-medium text-warning hover:bg-muted/40 transition-colors"
      >
        {open ? 'Hide' : 'Show'} {followUps.length} missed follow-up{followUps.length === 1 ? '' : 's'} (last 7 days)
      </button>
      {open && (
        <ul className="border-t border-border">
          {followUps.map(f => (
            <li key={f.prescription_id} className="flex items-center gap-3 px-4 py-1.5 min-h-10 border-b border-divider last:border-0">
              <span className="w-16 shrink-0 font-mono text-xs tabular-nums text-warning">{shortDate(f.due_date)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground truncate">
                  <button type="button" onClick={() => navigate(`/patients/${f.patient_id}`)} className="capitalize hover:underline underline-offset-2">
                    {f.patient_name}
                  </button>
                  <span className="ml-2 text-xs text-muted-foreground">{ageSex(f.patient_age, f.patient_date_of_birth, f.patient_gender)}</span>
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {[`was due ${shortDate(f.due_date)}`, showDoctor && f.doctor_name ? `Dr. ${f.doctor_name}` : null, f.diagnosis?.trim() || null].filter(Boolean).join(' · ')}
                </p>
              </div>
              <ContactButtons followUp={f} />
              {onBook && (
                <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs shrink-0" onClick={() => onBook(f)}>
                  Book
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactButtons({ followUp }: { followUp: FollowUpDue }) {
  const digits = (followUp.patient_phone || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return null;
  const msg = encodeURIComponent(`Hello ${followUp.patient_name}, this is a reminder for your follow-up visit.`);
  const link = 'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground shrink-0';
  return (
    <>
      <a href={`tel:+91${digits}`} className={link} title={`Call ${followUp.patient_phone}`} aria-label={`Call ${followUp.patient_name}`}>
        <Phone className="w-4 h-4" />
      </a>
      <a href={`https://wa.me/91${digits}?text=${msg}`} target="_blank" rel="noopener noreferrer" className={link} title="WhatsApp reminder" aria-label={`WhatsApp ${followUp.patient_name}`}>
        <MessageCircle className="w-4 h-4" />
      </a>
    </>
  );
}
